import * as genericService from "./genericFormServices";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";
import type { LegalAdviceFormSevenAData } from "../types/LegalAdvices/LegalAdviceFormSevenA.type";

const ENDPOINT = "legal-advice-form-seven-a";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormSevenAData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Seven A
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormSevenAData => {
  if (!item) return {} as LegalAdviceFormSevenAData;

  // Extract inner form values safely
  const form =
    item.legalAdviceFormSevenA ||
    item.legalAdviceFormSeven ||
    item.legalAdviceForm ||
    item.formData ||
    {};

  return {
    // Document Level Identifiers
    id: item.id || form.id || form.documentId || "",
    documentNumber: item.documentNumber || form.documentNumber || "",
    formType: item.formType || "LEGAL_ADVICE_FORM_SEVEN_A",
    status: item.status || form.status || "DRAFT",

    // Geo Levels
    originGeoLevel: item.originGeoLevel || form.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels)
      ? item.targetGeoLevels
      : Array.isArray(form.targetGeoLevels)
      ? form.targetGeoLevels
      : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || "",
    submitDate: form.submitDate || item.signedAt || item.updatedAt || "",
    createdAt: item.createdAt || form.createdAt || "",
    updatedAt: item.updatedAt || form.updatedAt || "",
    submittedAt: item.submittedAt || form.submittedAt || "",

    // Page 1: Items 1 to 16
    office: form.office || item.office || "",
    registerNo: form.registerNo || item.registerNo || "",
    adviceCaseNo: form.adviceCaseNo || item.adviceCaseNo || "",
    originalCourtAndCaseNo: form.originalCourtAndCaseNo || item.originalCourtAndCaseNo || "",
    prosecutedLawSection: form.prosecutedLawSection || item.prosecutedLawSection || "",
    chargedLawSection: form.chargedLawSection || item.chargedLawSection || "",
    appealCourtAndCaseNo: form.appealCourtAndCaseNo || item.appealCourtAndCaseNo || "",
    acquittalDate: form.acquittalDate || item.acquittalDate || "",
    regionStateCourtAndCaseNo: form.regionStateCourtAndCaseNo || item.regionStateCourtAndCaseNo || "",
    dismissalOrderDate: form.dismissalOrderDate || item.dismissalOrderDate || "",
    submitterName: form.submitterName || item.submitterName || "",
    applicationLetterDate: form.applicationLetterDate || item.applicationLetterDate || "",
    judgmentCopyApplyDate: form.judgmentCopyApplyDate || item.judgmentCopyApplyDate || "",
    judgmentCopyReceiveDate: form.judgmentCopyReceiveDate || item.judgmentCopyReceiveDate || "",
    reviewSubmitDeadline: form.reviewSubmitDeadline || item.reviewSubmitDeadline || "",
    reviewSubmitDate: form.reviewSubmitDate || item.reviewSubmitDate || "",
    respondentDetails: form.respondentDetails || item.respondentDetails || "",

    // Page 2: Summaries & Witness Statements
    caseSummary: form.caseSummary || item.caseSummary || "",
    prosecutionWitnessStatements: form.prosecutionWitnessStatements || item.prosecutionWitnessStatements || "",
    defenseWitnessStatements: form.defenseWitnessStatements || item.defenseWitnessStatements || "",
    courtWitnessStatements: form.courtWitnessStatements || item.courtWitnessStatements || "",
    advocateOpinionProsecution: form.advocateOpinionProsecution || item.advocateOpinionProsecution || "",
    advocateOpinionDefense: form.advocateOpinionDefense || item.advocateOpinionDefense || "",
    exhibitsReview: form.exhibitsReview || item.exhibitsReview || "",

    // Page 3: Court Review & Region/State Advocate Signatures
    courtJudgmentReviewOriginal: form.courtJudgmentReviewOriginal || item.courtJudgmentReviewOriginal || "",
    courtJudgmentReviewAppeal: form.courtJudgmentReviewAppeal || item.courtJudgmentReviewAppeal || "",
    courtJudgmentReviewHighCourt: form.courtJudgmentReviewHighCourt || item.courtJudgmentReviewHighCourt || "",
    appealFitnessOpinion: form.appealFitnessOpinion || item.appealFitnessOpinion || "",

    handlingSignName: form.handlingSignName || item.handlingSignName || "",
    handlingSubmitDate: form.handlingSubmitDate || item.handlingSubmitDate || "",
    handlingSignatureImage: formatSignatureUrl(form.handlingSignatureImage || item.handlingSignatureImage || ""),

    // Region/State Advocate Signature
    regionSignName: form.regionSignName || item.regionSignName || "",
    regionReceiveDate: form.regionReceiveDate || item.regionReceiveDate || "",
    regionSubmitDate: form.regionSubmitDate || item.regionSubmitDate || "",
    regionSignatureImage: formatSignatureUrl(form.regionSignatureImage || item.regionSignatureImage || ""),

    // Page 4: Central Officer Final Review
    centralOfficerOpinion: form.centralOfficerOpinion || item.centralOfficerOpinion || "",
    centralOfficerSignName: form.centralOfficerSignName || item.centralOfficerSignName || "",
    centralOfficerReceiveDate: form.centralOfficerReceiveDate || item.centralOfficerReceiveDate || "",
    centralOfficerSubmitDate: form.centralOfficerSubmitDate || item.centralOfficerSubmitDate || "",
    centralOfficerSignatureImage: formatSignatureUrl(form.centralOfficerSignatureImage || item.centralOfficerSignatureImage || ""),

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Seven A
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormSevenAData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;

  // Date Matching
  const formattedAppDate = app.submitDate || app.createdAt || "";

  const matchesDate =
    !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  // Search matching across Form Seven A fields
  const matchesGeneralSearch =
    !term ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term)) ||
    Boolean(app.registerNo && app.registerNo.toLowerCase().includes(term)) ||
    Boolean(app.adviceCaseNo && app.adviceCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.originalCourtAndCaseNo && app.originalCourtAndCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.prosecutedLawSection && app.prosecutedLawSection.toLowerCase().includes(term)) ||
    Boolean(app.chargedLawSection && app.chargedLawSection.toLowerCase().includes(term)) ||
    Boolean(app.submitterName && app.submitterName.toLowerCase().includes(term)) ||
    Boolean(app.office && app.office.toLowerCase().includes(term)) ||
    Boolean(app.regionSignName && app.regionSignName.toLowerCase().includes(term)) ||
    Boolean(app.centralOfficerSignName && app.centralOfficerSignName.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export const fetchLegalAdviceApplications = (
  params: FetchParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormSevenAData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormSevenAData> =>
  genericService.fetchApplicationById<LegalAdviceFormSevenAData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormSevenAData>
) => genericService.saveDraft<LegalAdviceFormSevenAData>(ENDPOINT, id, data);

export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormSevenAData
) => genericService.submitForm<LegalAdviceFormSevenAData>(ENDPOINT, id, data);

export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);